/**
 * Hook personnalisé pour la connexion WebSocket aux négociations temps réel
 * Gère la connexion, reconnexion automatique et les événements de négociation
 */
import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from './use-toast';
import { logger } from '../utils/logger';
import { getWsTicket } from '../utils/wsTicket';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Convertir l'URL HTTP en WS
const getWebSocketUrl = () => {
  const url = BACKEND_URL || 'http://localhost:8001';
  return url.replace('http://', 'ws://').replace('https://', 'wss://');
};

// Formater le message d'événement pour les toasts
const formatEventMessage = (eventType, data) => {
  const actor = data?.actor?.name || 'Quelqu\'un';
  const opNumber = data?.negotiation?.operationNumber || '';
  
  const messages = {
    'NEGOTIATION_CREATED': `${actor} a ouvert une négociation sur ${opNumber}`,
    'NEGOTIATION_OFFER_MADE': `${actor} a fait une nouvelle proposition sur ${opNumber}`,
    'NEGOTIATION_ACCEPTED': `${actor} a accepté la négociation sur ${opNumber}`,
    'NEGOTIATION_REJECTED': `${actor} a refusé la négociation sur ${opNumber}`,
    'NEGOTIATION_ESCALATED': `Négociation escaladée sur ${opNumber}`,
    'NEGOTIATION_MESSAGE_ADDED': `${actor} a ajouté un message sur ${opNumber}`,
  };
  
  return messages[eventType] || `Événement négociation: ${eventType}`;
};

/**
 * Hook pour se connecter aux notifications de négociation en temps réel
 * 
 * @param {Object} options - Options de configuration
 * @param {Function} options.onEvent - Callback appelé lors d'un événement de négociation
 * @param {Function} options.onConnect - Callback appelé lors de la connexion
 * @param {Function} options.onDisconnect - Callback appelé lors de la déconnexion
 * @param {boolean} options.showToasts - Afficher des notifications toast (défaut: true)
 * @returns {Object} - État et méthodes du WebSocket
 */
export const useNegotiationSocket = (options = {}) => {
  const { user } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [lastEvent, setLastEvent] = useState(null);
  const [connectionError, setConnectionError] = useState(null);
  
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const optionsRef = useRef(options);
  const maxReconnectAttempts = 5;
  const reconnectDelay = 3000;
  
  // Mettre à jour les options dans une ref
  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  // Effet principal pour la connexion WebSocket
  useEffect(() => {
    if (!user?.id) {
      return;
    }

    const {
      onEvent,
      onConnect,
      onDisconnect,
      showToasts = true
    } = optionsRef.current;

    const wsUrl = `${getWebSocketUrl()}/api/ws/negotiations/user/${user.id}?role=${user.role || 'client'}`;
    logger.debug('[NegotiationSocket] Connecting to:', wsUrl);

    let ws;

    const createConnection = async () => {
      try {
        // SEC-003 — Auth WS via ticket court-vie
        const ticket = await getWsTicket();
        if (!ticket) return;
        ws = new WebSocket(`${wsUrl}&token=${encodeURIComponent(ticket)}`);
        wsRef.current = ws;

        ws.onopen = () => {
          logger.debug('[NegotiationSocket] Connected');
          setIsConnected(true);
          setConnectionError(null);
          reconnectAttemptsRef.current = 0;
          onConnect?.();
        };

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            logger.debug('[NegotiationSocket] Message received:', data.type);
            
            if (data.type === 'negotiation_event') {
              setLastEvent(data);
              optionsRef.current.onEvent?.(data);
              
              // Afficher un toast si activé
              if (showToasts && data.event_type) {
                const message = formatEventMessage(data.event_type, data.data);
                toast({
                  title: '🔔 Négociation',
                  description: message,
                  duration: 5000
                });
              }
            }
          } catch (err) {
            console.error('[NegotiationSocket] Error parsing message:', err);
          }
        };

        ws.onerror = (error) => {
          console.error('[NegotiationSocket] Error:', error);
          setConnectionError('Erreur de connexion WebSocket');
        };

        ws.onclose = (event) => {
          logger.debug('[NegotiationSocket] Disconnected:', event.code, event.reason);
          setIsConnected(false);
          onDisconnect?.();
          
          // Reconnexion automatique si ce n'est pas une fermeture volontaire
          if (event.code !== 1000 && reconnectAttemptsRef.current < maxReconnectAttempts) {
            reconnectAttemptsRef.current++;
            logger.debug(`[NegotiationSocket] Reconnecting... (attempt ${reconnectAttemptsRef.current})`);
            
            reconnectTimeoutRef.current = setTimeout(() => {
              createConnection();
            }, reconnectDelay * reconnectAttemptsRef.current);
          }
        };

      } catch (err) {
        console.error('[NegotiationSocket] Connection failed:', err);
        setConnectionError('Impossible de se connecter au serveur');
      }
    };

    createConnection();

    // Heartbeat toutes les 30 secondes
    const pingInterval = setInterval(() => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000);

    // Cleanup
    return () => {
      logger.debug('[NegotiationSocket] Cleaning up...');
      clearInterval(pingInterval);
      
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      
      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmounted');
        wsRef.current = null;
      }
    };
  }, [user?.id, user?.role]);

  // Méthode pour s'abonner à une opération spécifique
  const subscribeToOperation = (operationId) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'subscribe_operation',
        operation_id: operationId
      }));
    }
  };

  return {
    isConnected,
    lastEvent,
    connectionError,
    subscribeToOperation
  };
};

/**
 * Hook pour se connecter à une room de négociation spécifique
 * 
 * @param {string} negotiationId - ID de la négociation
 * @param {Object} options - Options de configuration
 * @returns {Object} - État et méthodes du WebSocket
 */
export const useNegotiationRoomSocket = (negotiationId, options = {}) => {
  const { user } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [messages, setMessages] = useState([]);
  
  const wsRef = useRef(null);
  const optionsRef = useRef(options);
  
  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  useEffect(() => {
    if (!negotiationId || !user?.id) return;
    let ws;
    let stopped = false;

    const open = async () => {
      const ticket = await getWsTicket();
      if (!ticket || stopped) return;
      const wsUrl = `${getWebSocketUrl()}/api/ws/negotiations/${negotiationId}?user_id=${user.id}&token=${encodeURIComponent(ticket)}`;
      logger.debug('[NegotiationRoomSocket] Connecting to:', wsUrl);

      ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        logger.debug('[NegotiationRoomSocket] Connected to room:', negotiationId);
        setIsConnected(true);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'negotiation_update') {
            setMessages(prev => [...prev, data]);
            optionsRef.current.onUpdate?.(data);
          }
        } catch (err) {
          console.error('[NegotiationRoomSocket] Error parsing message:', err);
        }
      };

      ws.onclose = () => {
        logger.debug('[NegotiationRoomSocket] Disconnected from room:', negotiationId);
        setIsConnected(false);
      };
    };

    open();

    return () => {
      stopped = true;
      try { ws?.close(1000); } catch (e) { /* noop */ }
    };
  }, [negotiationId, user?.id]);

  return {
    isConnected,
    messages
  };
};

export default useNegotiationSocket;
